import { Box, Theme, Typography } from '@mui/material';
import { addHours, format } from 'date-fns';
import { uniq } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OrderItemStatus as OrderItemStatusType, Table as TableType } from '../../apiTypes';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import useMedia from '../../utils/useMedia';
import { TableSize, getTableSizes } from '../EditTableMap/EditTableMap';
import DeliveryOrdersList from './Delivery/DeliveryOrdersList';
import CloseShiftButton from './ShiftButtons/CloseShiftButton';

type Props = {
  table: TableType;
  tableSizes: TableSize;
};

const Table = ({ table, tableSizes }: Props) => {
  const navigate = useNavigate();
  const { isMobile } = useMedia();
  const { cafeSlug } = useAdmin();
  const { orders, reservations } = useCafe();

  const targetOrders = useMemo(() => orders.filter((o) => o.tableId === table.id), [orders, table.id]);

  const hasReservation = useMemo(
    () => reservations.some((r) => r.tableId === table.id && r.time > format(addHours(new Date(), -1), 'HH:mm')),
    [reservations, table.id]
  );

  const itemStatuses = useMemo(
    () =>
      uniq(
        targetOrders.reduce<OrderItemStatusType[]>(
          (acc, o) => [...acc, ...Object.values(o.items).map((i) => i.status)],
          []
        )
      ),
    [targetOrders]
  );

  const hasUnconfirmedOrder = useMemo(() => targetOrders.some((o) => o.status === 'ordered'), [targetOrders]);

  const getColor = useCallback(
    (t: Theme) => {
      if (hasUnconfirmedOrder) {
        return { main: t.palette.error.light, border: t.palette.error.main };
      }

      if (targetOrders.length === 0 && hasReservation) {
        return { main: t.palette.grey[800], border: t.palette.grey[900] };
      }

      if (targetOrders.length === 0) {
        return { main: t.palette.grey[500], border: t.palette.grey[700] };
      }

      if (itemStatuses.includes('ready')) {
        return { main: t.palette.error.light, border: t.palette.error.main };
      }

      if (itemStatuses.length === 1 && itemStatuses[0] === 'complete') {
        return { main: t.palette.success.light, border: t.palette.success.main };
      }

      if (itemStatuses.includes('ordered') || itemStatuses.includes('processing')) {
        return { main: t.palette.secondary.main, border: t.palette.secondary.dark };
      }

      return { main: t.palette.secondary.main, border: t.palette.secondary.dark };
    },
    [hasUnconfirmedOrder, targetOrders.length, hasReservation, itemStatuses]
  );

  return (
    <Box
      key={table.id}
      id={`table-${table.id}`}
      onClick={() => navigate(`/${cafeSlug}/pos/orders/?table=${table.id}`)}
      sx={{
        position: 'absolute',
        top: `${table.y * 100}%`,
        left: `${table.x * 100}%`,
        backgroundColor: (t) => getColor(t).main,
        border: (t) => `2px solid ${getColor(t).border}`,
        width: tableSizes[table.type].width,
        height: tableSizes[table.type].height,
        borderRadius: table.type === 'circle' ? '50%' : '3px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        userSelect: 'none',
        cursor: 'pointer',
      }}
    >
      <Typography variant={isMobile ? 'body2' : 'body1'} color="white" textAlign="center">
        {table.name}
      </Typography>
    </Box>
  );
};

const TablesMap = () => {
  const { tablesMap } = useAdmin();
  const { isMobile } = useMedia();
  const { deliveryOrders } = useCafe();
  const { worker, currentPosShift } = useCafe();

  const mapRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(0);
  const { bgUrl, tables, scale } = tablesMap;

  const mapRect = mapRef.current?.getBoundingClientRect() || { width: 500, height: 500, top: 0, left: 0 };
  const tableSizes = getTableSizes(mapRef.current?.getBoundingClientRect().width || 500, scale);

  useEffect(() => {
    setMounted(1);
    const id = setTimeout(() => setMounted(2), 500);
    return () => clearTimeout(id);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
      <DeliveryOrdersList deliveryOrders={deliveryOrders} hasAddButton />
      <Box sx={{ width: '100%' }}>
        <Box sx={{ textAlign: 'right' }}>
          {currentPosShift && worker?.credentials.open_close_pos_shifts && <CloseShiftButton />}
        </Box>

        <Box
          ref={mapRef}
          sx={{
            position: 'relative',
            width: '100%',
            height: mapRect.width * 0.7,
            backgroundBlendMode: 'multiply',
            backgroundColor: (t) => `${t.palette.background.default}`,
            backgroundImage: bgUrl ? `url(${bgUrl})` : 'none',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            borderRadius: 1,
            overflow: 'hidden',
            mb: 2,
            mx: 'auto',
          }}
        >
          {mounted && tables.map((table) => <Table key={table.id} table={table} tableSizes={tableSizes} />)}
        </Box>
      </Box>
    </Box>
  );
};

export default TablesMap;
